// ─────────────────────────────────────────────────────────────────────────────
// Client 360 — TypeScript types
// ─────────────────────────────────────────────────────────────────────────────

export interface ClientEntityIds {
  contactId: string | null;
  customerId: string | null;
  accPartyId: string | null;
  clientId: string | null;
}

export interface ClientAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface SubContact {
  name: string;
  designation: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
}

export interface ClientProfile {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  gstNumber: string;
  panNumber: string;
  address: ClientAddress;
  shippingAddress: ClientAddress | null;
  contactType: 'individual' | 'organization';
  source: string;
  status: string;
  ownerId: string | null;
  subContacts: SubContact[];
  entityIds: ClientEntityIds;
}

export interface ChannelChainEntry {
  role: string;
  id: string;
}

export interface ClientChannelInfo {
  channelCategory: string;
  upstreamChain: ChannelChainEntry[];
  priceTier: string;
}

export interface AgingBuckets {
  current: number;
  '1-30': number;
  '31-60': number;
  '61-90': number;
  '90+': number;
}

export interface ClientFinancials {
  creditLimit: number;
  outstanding: number;
  aging: AgingBuckets;
  lifetimeRevenue: number;
  lifetimeOrders: number;
  // Gap-audit follow-up: the summary numbers Account 360 already showed.
  averageOrderValue: number;
  lastOrderDate: string | null;
  lastOrderValue: number | null;
  pendingPayments: number;
  /** Credit standing — the numbers the invoice credit approval uses (2026-11-04). */
  credit?: ClientCredit | null;
}

export interface ClientCredit {
  limit: number | null;
  creditDays: number;
  invoiceOutstanding: number;
  /** Old dues from before the CRM (Contact.previousCreditBalance). */
  oldDues: number;
  outstanding: number;
  overdueAmount: number;
  overdueCount: number;
  oldestOverdue: { number: string; dueDate: string; days: number } | null;
  available: number | null;
  /** A new bill would need Admin / Super Admin credit approval before posting. */
  needsApproval: boolean;
  reasons: Array<'over_limit' | 'overdue'>;
  message: string;
  advancesReceived: number;
}

export interface AdvanceRecord {
  orderId: string; orderNumber: string; documentType: string; orderTotal: number;
  amount: number; method: string; reference: string; date: string | null; recordedByName: string; hasProof: boolean;
}

export interface CreditClearance {
  kind: 'invoice' | 'dispatch'; id: string; number: string; invoiceNumber: string; amount: number;
  requestedByName: string; at: string | null; outcome: 'approved' | 'rejected' | 'pending' | string;
  reason: string; byName: string; note: string;
}

export interface ClientPipelineSummary {
  enquiries: number;
  quotations: number;
  won: number;
  lost: number;
}

export interface ClientFollowupSummary {
  next: string | null;
  last: string | null;
}

export interface DocumentBase {
  _id: string;
  date?: string;
  createdAt?: string;
  status?: string;
  /** Identifies which storage flow owns the document for safe detail routing. */
  client360Source?: 'sales-order' | 'accounts-invoice' | 'commercial' | 'delivery';
}

export interface QuotationDoc extends DocumentBase {
  quotationNo?: string;
  /** SalesOrderModel's own number field, used by the live pipeline. */
  orderNumber?: string;
  documentNumber?: string;
  customerName?: string;
  grandTotalMinor?: number;
  grandTotal?: number;
}

export interface OrderDoc extends DocumentBase {
  orderNo?: string;
  documentNumber?: string;
  customerName?: string;
  grandTotalMinor?: number;
  grandTotal?: number;
}

export interface InvoiceDoc extends DocumentBase {
  invoiceNo?: string;
  invoiceNumber?: string;
  customerName?: string;
  grandTotalMinor?: number;
  grandTotal?: number;
  amountDue?: number;
  outstandingMinor?: number;
}

export interface DeliveryDoc extends DocumentBase {
  deliveryNo?: string;
  documentNumber?: string;
  customerName?: string;
  carrier?: string;
  trackingNumber?: string;
}

export interface PaymentDoc extends DocumentBase {
  paymentNumber?: string;
  amount?: number;
  paymentMethod?: string;
  referenceNumber?: string;
  /** Proof of payment the customer sent — receipt, screenshot, bank advice. */
  attachments?: { fileUrl: string; originalName?: string; mimeType?: string; size?: number }[];
}

export interface CreditNoteDoc extends DocumentBase {
  creditNoteNo?: string;
  grandTotalMinor?: number;
  reason?: string;
  customerName?: string;
}

export interface SampleDoc extends DocumentBase {
  code?: string;
  customerName?: string;
  lines?: Array<{ productName: string; packSize: string; quantity: number }>;
}

export interface ComplaintDoc extends DocumentBase {
  ticketNumber?: string;
  title?: string;
  priority?: string;
  cmsNo?: string;
  cmsStatus?: string;
}

export interface CustomerPurchaseOrder {
  _id: string;
  poNumber: string;
  poDate: string | null;
  /** Which of OUR documents it arrived on. */
  againstNumber: string;
  againstType: string;
  attachments: { url: string; name: string }[];
}

export interface ClientDocuments {
  /** The customer's own POs, gathered from records and orders alike. */
  customerPurchaseOrders: CustomerPurchaseOrder[];
  salesHandoffs?: Array<{ orderId: string; orderNumber: string; paymentArrangement: string; creditAmount: number | null; amountReceived: number; balanceAmount: number; handedOffAt?: string }>;
  dispatchAttachments?: Array<{ url: string; name: string; kind: string; orderNumber: string; orderId: string }>;
  quotations: QuotationDoc[];
  /** Proforma invoices — SalesOrderModel rows with documentType 'proforma'. */
  proformas: QuotationDoc[];
  orders: OrderDoc[];
  invoices: InvoiceDoc[];
  deliveries: DeliveryDoc[];
  payments: PaymentDoc[];
  /** Advance / payment records captured on this client's orders. */
  advances?: AdvanceRecord[];
  /** Credit approvals on invoices and dispatch-on-credit clearances. */
  creditClearances?: CreditClearance[];
  creditNotes: CreditNoteDoc[];
  samples: SampleDoc[];
  complaints: ComplaintDoc[];
}

export interface LeadActivity {
  _id: string;
  name?: string;
  phone?: string;
  status?: string;
  niche?: string;
  location?: string;
  created_at?: string;
}

export interface DealActivity {
  _id: string;
  title: string;
  stage?: string;
  value?: number;
  created_at?: string;
}

export interface CallActivity {
  _id: string;
  disposition?: string;
  notes?: string;
  duration_sec?: number;
  created_at?: string;
}

export interface MeetingActivity {
  _id: string;
  title: string;
  scheduled_at?: string;
  status?: string;
}

export interface FollowupActivity {
  _id: string;
  notes?: string;
  follow_up_date?: string;
  created_at?: string;
}

export interface ClientActivity {
  leads: LeadActivity[];
  deals: DealActivity[];
  calls: CallActivity[];
  meetings: MeetingActivity[];
  followups: FollowupActivity[];
}

export interface LoyaltyTransaction {
  _id: string;
  type: 'earned' | 'redeemed' | 'expired' | 'adjusted';
  points: number;
  balance: number;
  referenceNumber?: string;
  narration?: string;
  createdAt?: string;
}

export interface ClientLoyalty {
  balance: number;
  totalEarned: number;
  totalRedeemed: number;
  transactions: LoyaltyTransaction[];
}

export interface VoucherRedemption {
  _id: string;
  voucherCode: string;
  discountAppliedMinor: number;
  documentNumber?: string;
  redeemedAt?: string;
}

export interface ClientVouchers {
  redemptions: VoucherRedemption[];
}

export interface Client360Response {
  profile: ClientProfile;
  channelInfo: ClientChannelInfo;
  financials: ClientFinancials;
  pipeline: ClientPipelineSummary;
  followupSummary: ClientFollowupSummary;
  documents: ClientDocuments;
  activity: ClientActivity;
  loyalty: ClientLoyalty;
  vouchers: ClientVouchers;
}

// ── List types ───────────────────────────────────────────────────────────────

export interface ClientListItem {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  gstNumber?: string;
  channelCategory?: string;
  creditLimit?: number;
  isActive?: boolean;
  outstanding?: number;
  createdAt?: string;
}

export interface ClientListResponse {
  items: ClientListItem[];
  total: number;
  limit: number;
  skip: number;
}

export interface ClientListFilters {
  search?: string;
  channel?: string;
  hasOutstanding?: boolean;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  skip?: number;
}

export const CHANNEL_TIERS = [
  'DirectCustomer',
  'CustomerFromChannel',
  'Reseller',
  'Wholesaler',
  'Dealer',
  'Distributor',
  'Stockist',
  'NationalDistributor',
] as const;

export type ChannelTier = (typeof CHANNEL_TIERS)[number];
